import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LinkIcon from "@mui/icons-material/Link";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { simulationService } from "../../../services/SimulationService";
import { SimulationPortalLinkResponseDTO } from "../../../interfaces/DTO/public/simulationPortalLink";

interface Props {
  simulationId: number;
  buttonVariant?: "icon" | "text";
  buttonText?: string;
}

export const SimulationPortalLinkDialog: React.FC<Props> = ({
  simulationId,
  buttonVariant = "icon",
  buttonText = "לינק חד פעמי ללקוח",
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SimulationPortalLinkResponseDTO | null>(null);
  const [copied, setCopied] = useState(false);

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    setError(null);
    try {
      const existing = await simulationService.getPortalLink(simulationId);
      setResult(existing);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "שגיאה בטעינת לינק קיים");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setCopied(false);
  };

  const createLink = async () => {
    setLoading(true);
    setError(null);
    setCopied(false);
    try {
      const data = await simulationService.createPortalLink(simulationId);
      setResult(data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "שגיאה ביצירת לינק");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!result?.link) return;
    await navigator.clipboard.writeText(result.link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleOpenTab = () => {
    if (result?.link) window.open(result.link, "_blank", "noopener,noreferrer");
  };

  const handleRevoke = async () => {
    setRevoking(true);
    setError(null);
    try {
      await simulationService.revokePortalLink(simulationId);
      setResult(null);
      setCopied(false);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "שגיאה בביטול הלינק");
    } finally {
      setRevoking(false);
    }
  };

  const formatDate = (value: string) =>
    new Date(value).toLocaleDateString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const expiryDate = result ? formatDate(result.expires_at) : null;
  const visitedDate = result?.last_visited_at ? formatDate(result.last_visited_at) : null;

  return (
    <>
      {buttonVariant === "icon" ? (
        <Tooltip title={buttonText}>
          <IconButton size="small" onClick={handleOpen} color="primary">
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Button size="small" variant="outlined" startIcon={<LinkIcon fontSize="small" />} onClick={handleOpen}>
          {buttonText}
        </Button>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth dir="rtl">
        <DialogTitle>
          לינק פורטל חד פעמי ללקוח
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            מוגבל לסימולציה זו בלבד
          </Typography>
        </DialogTitle>

        <DialogContent>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}

          {!loading && !result && !error && (
            <Box sx={{ py: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                אין לינק פעיל עבור סימולציה זו
              </Typography>
              <Button variant="contained" startIcon={<LinkIcon />} onClick={createLink}>
                צור לינק
              </Button>
            </Box>
          )}

          {!loading && result && (
            <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="לינק ללקוח"
                value={result.link}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={copied ? "הועתק!" : "העתק"}>
                        <IconButton onClick={handleCopy} edge="end" color={copied ? "success" : "default"}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                sx={{ direction: "ltr" }}
              />

              <Typography variant="caption" color="text.secondary">
                תוקף עד: {expiryDate}
              </Typography>

              <Typography variant="caption" color={visitedDate ? "success.main" : "text.disabled"}>
                {visitedDate ? `הלקוח ביקר בלינק בתאריך: ${visitedDate}` : "הלקוח עדיין לא ביקר בלינק"}
              </Typography>

              {copied && (
                <Alert severity="success" sx={{ py: 0.5 }}>
                  הלינק הועתק ללוח
                </Alert>
              )}

              <Divider />

              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  color="warning"
                  startIcon={<RefreshIcon />}
                  onClick={createLink}
                  disabled={loading || revoking}
                >
                  צור לינק חדש
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={revoking ? <CircularProgress size={14} color="inherit" /> : <DeleteOutlineIcon />}
                  onClick={handleRevoke}
                  disabled={loading || revoking}
                >
                  בטל לינק
                </Button>
              </Box>
              <Typography variant="caption" color="text.disabled">
                יצירת לינק חדש או ביטול הלינק יבטלו את הלינק הקיים
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {result && (
            <Button startIcon={<OpenInNewIcon />} onClick={handleOpenTab} variant="outlined">
              פתח בטאב חדש
            </Button>
          )}
          <Button onClick={handleClose}>סגור</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SimulationPortalLinkDialog;
